import { FC, useEffect } from 'react';
import {
  Chip,
  CommentsPreview,
  ExternalLink,
  Loader,
  Modal,
  NothingFoundAlert,
  ReactionsPreview,
  SelectInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ModalProps, useModalContext } from 'store/modal';
import { useNew, useUpdateNew } from 'store/news';
import { Box, List, ListItem, Stack } from '@mui/material';
import moment from 'moment';
import { ReactionsModal } from 'containers/reactions';
import { ContentLanguage } from 'types/general';
import { Controller, useForm } from 'react-hook-form-latest';
import { includes, resolveTranslationByLanguage, upper } from 'utils/general';
import { APP } from 'config';
import { formatLanguage } from 'utils/format';
import RichTextEditor from 'components/common/RichTextEditor/RichTextEditor';
import { SystemColors } from 'melp-design/style';
import { useCreateComment } from 'store/comments';
import { binaries, Binary } from 'types/Common';
import { CommentsFeed } from './atoms';

interface Values {
  language: Uppercase<ContentLanguage>;
  allowComments: Binary;
  comment: string;
}

interface Props extends ModalProps {
  newId: string;
}

export const NewsThreadModal: FC<Props> = ({ newId, closeModal }) => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();

  const { data: news, isLoading } = useNew(newId);

  const {
    control,
    getValues,
    reset,
    watch,
    formState: { dirtyFields },
  } = useForm<Values>({
    defaultValues: {
      language: upper(APP.locales.default),
      allowComments: 'yes',
      comment: '',
    },
  });

  useEffect(() => {
    if (news) {
      const userLanguage = i18n.language.toUpperCase();
      reset({
        language: includes(news.supportedLanguages, userLanguage)
          ? userLanguage
          : upper(APP.locales.default),
        allowComments: news.allowComments ? 'yes' : 'no',
      });
    }
  }, [i18n.language, news, reset]);

  const { mutate: createComment, isLoading: isCreatingComment } =
    useCreateComment();
  const { mutate: updateNew, isLoading: isUpdating } = useUpdateNew();

  if (isLoading) {
    return (
      <Modal.Content>
        <Loader />
      </Modal.Content>
    );
  }
  if (!news) {
    return (
      <Modal.Content>
        <NothingFoundAlert />
      </Modal.Content>
    );
  }

  const [language] = watch(['language']);
  const isDirty = !!Object.keys(dirtyFields).length;
  const content = resolveTranslationByLanguage(news.translations, language);

  return (
    <Modal.Content
      title={content?.title || news.name}
      dividers
      header={
        <Stack
          flexDirection="row"
          columnGap={4}
          rowGap={1}
          alignItems="center"
          flexWrap="wrap"
        >
          {news.publishedAt ? (
            <Typography color="textSecondary">
              {moment(news.publishedAt).format('l LT')}
            </Typography>
          ) : null}
          {news.commentsCount ? (
            <CommentsPreview count={news.commentsCount} />
          ) : null}
          {news.reactions.total ? (
            <ReactionsPreview
              reactions={news.reactions.summary}
              onClick={() => {
                openModal({
                  component: ReactionsModal,
                  props: { hostId: newId },
                });
              }}
            />
          ) : null}
          <Stack flexDirection="row" gap={1}>
            {news.pinned ? (
              <Chip label={t('news.pinned.title')} variant="warning" />
            ) : null}
            {news.categories.length ? (
              <Stack flexDirection="row" gap={1}>
                {news.categories.map((category, i) => (
                  <Chip label={category.name} variant="neutral" key={i} />
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Stack>
      }
      footer={
        news.allowComments ? (
          <Controller
            name="comment"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('clients.comment')}
                name={name}
                value={value}
                onChange={onChange}
                rows={2}
                sx={{ width: '100%' }}
                ref={ref}
              />
            )}
          />
        ) : null
      }
      actions={[
        {
          variant: 'neutral-outline',
          onClick: () => closeModal({ action: 'CLOSE' }),
          label: t('buttons.ok'),
        },
        ...(news.allowComments
          ? [
              {
                variant: 'primary',
                label: t('common.submit'),
                onClick: () => {
                  createComment(
                    {
                      entity: 'news',
                      subjectId: newId,
                      content: getValues('comment'),
                    },
                    {
                      onSuccess: () => {
                        reset({ comment: '' }, { keepDirty: false });
                      },
                    },
                  );
                },
                disabled: isCreatingComment || !isDirty,
              } as const,
            ]
          : []),
      ]}
    >
      {content ? (
        <Stack gap={2} alignItems="flex-start">
          <Stack
            flexDirection="row"
            justifyContent="flex-start"
            columnGap={2}
            rowGap={1}
            flexWrap="wrap"
          >
            <Controller
              name="language"
              control={control}
              render={({ field: { name, value, onChange, ref } }) => (
                <Box minWidth={200}>
                  <SelectInput
                    label={t('marketplaceItems.translationLanguage')}
                    name={name}
                    value={value}
                    onChange={onChange}
                    options={news.supportedLanguages.map((language) => ({
                      value: language,
                      label: formatLanguage(i18n.language, language),
                    }))}
                    ref={ref}
                  />
                </Box>
              )}
            />

            <Controller
              name="allowComments"
              control={control}
              render={({ field: { name, value, ref } }) => (
                <Box minWidth={200}>
                  <SelectInput
                    label={t('news.allowComments.label')}
                    name={name}
                    value={value}
                    onChange={(value) => {
                      updateNew({
                        newId,
                        update: {
                          allowComments: value === 'yes',
                        },
                      });
                    }}
                    options={binaries.map((binary) => ({
                      value: binary,
                      label: t(`common.${binary}`),
                    }))}
                    disabled={isUpdating}
                    ref={ref}
                  />
                </Box>
              )}
            />
          </Stack>

          {content.headerImage?.signedUrl ? (
            <img
              src={content.headerImage.signedUrl}
              style={{ maxHeight: 300, maxWidth: '100%' }}
              alt=""
            />
          ) : null}

          {content.content ? (
            <Box sx={{ py: 1 }}>
              <RichTextEditor value={content.content} displayMode />
            </Box>
          ) : null}

          {content.attachments.length ? (
            <Box>
              <Typography>{t('sidebar.more')}:</Typography>
              <List>
                {content.attachments.map((attachment) => (
                  <ListItem sx={{ px: 0, py: 0.5 }} key={attachment.id}>
                    <ExternalLink
                      label={`📎 ${attachment.fileName}`}
                      href={attachment.signedUrl}
                      key={attachment.id}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : null}
        </Stack>
      ) : null}

      <Stack
        flexDirection="row"
        alignItems="center"
        gap={2}
        sx={{
          py: 1,
          my: 1,
          borderBottom: `1px solid ${SystemColors.grey[90]}`,
        }}
      >
        <Typography variant="h3">
          {t('comments.title')} ({news.commentsCount})
        </Typography>

        {news.reactions.total ? (
          <ReactionsPreview
            reactions={news.reactions.summary}
            onClick={() => {
              openModal({
                component: ReactionsModal,
                props: { hostId: newId },
              });
            }}
          />
        ) : null}
      </Stack>

      <CommentsFeed newId={newId} />
    </Modal.Content>
  );
};
